/**
 * Carrier MCP — Stripe Connect tools (Phase 16).
 *
 * Provides AI-agent access to the operator Stripe Connect layer.
 * All tools call the Stripe API directly using STRIPE_SECRET_KEY from env.
 * The connected account ID is looked up from CARRIER_USERS KV
 * under key `stripe_account:<operatorId>`.
 *
 * Tools registered here:
 *   stripe_connect_status        — read-only: account status + capabilities
 *   stripe_connect_payouts       — read-only: list recent payouts
 *   stripe_connect_balance       — read-only: available + pending balance
 *   stripe_connect_refund        — write (HARD_BLOCK): refund a charge
 *   stripe_connect_dispute_list  — read-only: list active disputes
 *   radar_review_list            — read-only: list pending Radar reviews
 *   radar_review_approve         — admin (HARD_BLOCK): approve a review
 *   radar_review_decline         — admin (HARD_BLOCK): decline/close a review
 *   radar_value_list_add         — admin (HARD_BLOCK): add to block/allow list
 *   radar_rule_toggle            — admin: documented stub (Stripe API limitation)
 *
 * HARD_BLOCK pattern: destructive tools require a confirm_token stored in
 * OAUTH_KV with a 5-min TTL (same pattern as pricing-tools.ts).
 *
 * SCOPES ARE ENFORCED AT CALL TIME, NOT BY THE CATALOGUE
 * -----------------------------------------------------
 * Nothing here goes through `wrapHandler`, and `tool-list-scope-filter.ts` says
 * in its own header that hiding a tool from tools/list is not a closed door — a
 * client may call a tool the list never showed it. So every handler below opens
 * with `denyUnlessScoped`, enforcing the `STRIPE_CONNECT_TOOL_SCOPES` table that
 * was already declared right here and previously used only to build that filter.
 *
 * The five `radar_*` tools carry a second gate. They call Stripe with no
 * `Stripe-Account` header, so they act on Carrier's own platform account and
 * touch every customer's fraud reviews at once. Scope cannot express that: an
 * enterprise org legitimately holds `admin` over its OWN data. Over stdio
 * `isPlatformOperator` fails closed, so they refuse outright — correct for a
 * process holding exactly one tenant's credentials.
 *
 * This is the stdio half of #788, which landed the same guard on the Worker's
 * copy of this file and on both copies of wallet-tools.ts, but not on this one.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import * as Sentry from "@sentry/cloudflare";
import type { Env, CarrierProps, ToolScope } from "./types.js";
import { writeAudit } from "./audit.js";
import { annotationsFor } from "./tool-annotations.js";
import { isPlatformOperator } from "./billing.js";
import { denyUnlessScoped } from "./scope-guard.js";
import { requireBinding } from "./binding-unavailable.js";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface StripeConnectToolContext {
  env: Env;
  props: CarrierProps;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Hard-block confirm token stored in KV with 5-min TTL. */
async function verifyConfirmToken(
  env: Env,
  sub: string,
  toolName: string,
  token: string,
): Promise<boolean> {
  // Both halves of the confirm round-trip need somewhere to keep the token for
  // five minutes, and stdio stubs OAUTH_KV to null. Reading `.put` off it threw
  // a bare "Cannot read properties of null (reading 'put')" out of
  // stripe_connect_refund — a money-moving tool reporting what looked like a
  // crash. Refusing is the only correct answer: a single-use token that cannot
  // be stored cannot be verified, and skipping the check on a refund is not an
  // option.
  requireBinding(
    env.OAUTH_KV,
    toolName,
    "OAUTH_KV",
    "the short-lived confirmation token this operation requires",
  );
  const key = `confirm:${sub}:${toolName}`;
  const stored = await env.OAUTH_KV.get(key);
  if (!stored || stored !== token) return false;
  await env.OAUTH_KV.delete(key); // single-use
  return true;
}

async function issueConfirmToken(env: Env, sub: string, toolName: string): Promise<string> {
  requireBinding(
    env.OAUTH_KV,
    toolName,
    "OAUTH_KV",
    "the short-lived confirmation token this operation requires",
  );
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const token = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  const key = `confirm:${sub}:${toolName}`;
  await env.OAUTH_KV.put(key, token, { expirationTtl: 300 }); // 5 min TTL
  return token;
}

type ToolResult = { content: Array<{ type: "text"; text: string }>; isError?: boolean };

function ok(text: string): ToolResult {
  return { content: [{ type: "text", text }] };
}

function err(text: string): ToolResult {
  return { isError: true, content: [{ type: "text", text }] };
}

/** Call Stripe REST API (GET). */
async function stripeGet<T = unknown>(
  stripeKey: string,
  path: string,
  connectedAccountId?: string,
): Promise<{ ok: boolean; status: number; data: T }> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${stripeKey}`,
    "Stripe-Version": "2025-08-27.basil",
  };
  if (connectedAccountId) headers["Stripe-Account"] = connectedAccountId;
  const res = await fetch(`https://api.stripe.com${path}`, { headers });
  const data = (await res.json()) as T;
  return { ok: res.ok, status: res.status, data };
}

/** Call Stripe REST API (POST, form-encoded). */
async function stripePost<T = unknown>(
  stripeKey: string,
  path: string,
  params: Record<string, string>,
  connectedAccountId?: string,
  idempotencyKey?: string,
): Promise<{ ok: boolean; status: number; data: T }> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${stripeKey}`,
    "Stripe-Version": "2025-08-27.basil",
    "Content-Type": "application/x-www-form-urlencoded",
  };
  if (connectedAccountId) headers["Stripe-Account"] = connectedAccountId;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  const res = await fetch(`https://api.stripe.com${path}`, {
    method: "POST",
    headers,
    body: new URLSearchParams(params).toString(),
  });
  const data = (await res.json()) as T;
  return { ok: res.ok, status: res.status, data };
}

/**
 * Idempotency key for a refund, derived from the refund INTENT.
 *
 * Deliberately not the confirm_token. That token is deleted by
 * verifyConfirmToken BEFORE the Stripe call, so a caller whose response was
 * lost gets a brand-new token on the retry — keying on it would hand Stripe a
 * different key and issue a second refund, which is the bug this closes.
 *
 * Keyed on the intent, a retry inside Stripe's 24h window returns the ORIGINAL
 * refund. Two genuinely different refunds on one charge differ in amount or
 * reason and so still get through; two byte-identical partial refunds inside
 * 24h collapse into one, which is the safe direction for money leaving an
 * operator's account.
 */
function refundIdempotencyKey(
  sub: string,
  charge: string,
  amount?: number,
  reason?: string,
): string {
  return `carrier-refund:${sub}:${charge}:${amount ?? "full"}:${reason ?? "none"}`;
}

/** Doc + registry merge — mirrors the tool inventory in this file's header comment. */
export const STRIPE_CONNECT_TOOL_SCOPES: Record<string, ToolScope> = {
  stripe_connect_status: "read",
  stripe_connect_payouts: "read",
  stripe_connect_balance: "read",
  stripe_connect_refund: "write",
  stripe_connect_dispute_list: "read",
  radar_review_list: "read",
  radar_review_approve: "admin",
  radar_review_decline: "admin",
  radar_value_list_add: "admin",
  radar_rule_toggle: "admin",
};

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerStripeConnectTools(
  server: McpServer,
  ctx: StripeConnectToolContext,
): void {
  const { env, props } = ctx;
  const operatorId = props.org_id ?? props.sub;
  const kvKey = `stripe_account:${operatorId}`;

  /** Look up the operator's connected Stripe account ID from KV. */
  async function getAccountId(): Promise<string | null> {
    return env.CARRIER_USERS.get(kvKey);
  }

  // -------------------------------------------------------------------------
  // stripe_connect_status
  // -------------------------------------------------------------------------

  server.registerTool(
    "stripe_connect_status",
    {
      title: "Stripe Connect Status",
      description:
        "Read-only: returns the Stripe Connect account status, capabilities, and requirements for the authenticated operator.",
      inputSchema: {
        operator_id: z
          .string()
          .optional()
          .describe("Override operator_id (admin use). Defaults to caller's org/user."),
      },
      annotations: annotationsFor("stripe_connect_status", "read"),
    },
    async ({ operator_id }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "stripe_connect_status", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;
      // `operator_id` was documented "admin use" and enforced by nothing, so any
      // caller could name another operator's id and read that operator's Stripe
      // Connect status. The description was the only thing standing in the way
      // of a cross-tenant read, and a description is not an access control.
      //
      // Fixed in the Worker earlier (apps/mcp-server, #763) and missed here,
      // which is the third time in one night that a fix landed on one copy of
      // this fork and not the other.
      //
      // Refuse rather than silently falling back to the caller's own id: a
      // genuine operator passing an id and quietly getting their own data back
      // is a debugging trap, and the refusal is what a test can assert.
      //
      // In stdio this refuses every override, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails closed.
      // That is the right answer for a process holding one tenant's credentials.
      if (operator_id !== undefined && operator_id !== operatorId) {
        if (!isPlatformOperator(env, props.org_id)) {
          return err(
            "operator_id may only be overridden by Carrier's own organization. " +
              "Omit it to read your own Stripe Connect status.",
          );
        }
      }
      const opId = operator_id ?? operatorId;
      const opKvKey = operator_id ? `stripe_account:${operator_id}` : kvKey;
      const start = Date.now();
      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");

        const accountId = await env.CARRIER_USERS.get(opKvKey);
        if (!accountId) {
          return ok(JSON.stringify({ status: "not_connected", operator_id: opId }));
        }

        const result = await stripeGet<{
          id: string;
          charges_enabled: boolean;
          payouts_enabled: boolean;
          details_submitted: boolean;
          requirements: unknown;
          capabilities: unknown;
          default_currency: string;
          settings?: { payouts?: { schedule?: { interval: string; delay_days: number } } };
        }>(stripeKey, `/v1/accounts/${accountId}`);

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);
        const a = result.data;
        const status = a.charges_enabled && a.details_submitted ? "active" : "pending";

        writeAudit(env, {
          tool_name: "stripe_connect_status",
          ocs_method: "stripe.account.read",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(JSON.stringify({
          status,
          operator_id: opId,
          account_id: accountId,
          charges_enabled: a.charges_enabled,
          payouts_enabled: a.payouts_enabled,
          details_submitted: a.details_submitted,
          requirements: a.requirements,
          capabilities: a.capabilities,
          default_currency: a.default_currency,
          payout_schedule: a.settings?.payouts?.schedule,
        }));
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // stripe_connect_payouts
  // -------------------------------------------------------------------------

  server.registerTool(
    "stripe_connect_payouts",
    {
      title: "List Stripe Payouts",
      description:
        "List money Stripe has already sent from the operator's connected account to their bank, newest first. Read-only. " +
        "Params: `limit` (1-50, default 10), `status` (optional Stripe payout status, e.g. 'paid', 'pending', 'failed'). " +
        "Returns: { payouts: [{ id, amount, currency, status, arrival_date, automatic, created }], has_more } — amount in the currency's smallest unit. " +
        "Do NOT use this for money still sitting at Stripe and not yet paid out — use `stripe_connect_balance`.",
      inputSchema: {
        limit: z.number().int().min(1).max(50).default(10).describe("Number of payouts to return."),
        status: z
          .enum(["pending", "paid", "failed", "canceled", "in_transit"])
          .optional()
          .describe("Filter by payout status."),
      },
      annotations: annotationsFor("stripe_connect_payouts", "read"),
    },
    async ({ limit, status }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "stripe_connect_payouts", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;
      const start = Date.now();
      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");
        const accountId = await getAccountId();
        if (!accountId) return err("No connected Stripe account found.");

        const params = new URLSearchParams({ limit: String(limit) });
        if (status) params.set("status", status);

        const result = await stripeGet<{
          data: Array<{
            id: string;
            amount: number;
            currency: string;
            status: string;
            arrival_date: number;
            automatic: boolean;
            created: number;
          }>;
          has_more: boolean;
        }>(stripeKey, `/v1/payouts?${params.toString()}`, accountId);

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: "stripe_connect_payouts",
          ocs_method: "stripe.payouts.list",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(JSON.stringify({ payouts: result.data.data, has_more: result.data.has_more }));
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // stripe_connect_balance
  // -------------------------------------------------------------------------

  server.registerTool(
    "stripe_connect_balance",
    {
      title: "Stripe Connect Balance",
      description:
        "Read the money sitting in the operator's connected Stripe account: available and pending, split per currency. " +
        "Params: none (the connected account is resolved from the session). " +
        "Returns: { account_id, available: [{ amount, currency }], pending: [{ amount, currency }] } — amounts in the currency's smallest unit (cents for EUR/USD). " +
        "Do NOT use this for the Carrier prepaid wallet — use `wallet_balance`. " +
        "Do NOT use this for money already paid out to the bank — use `stripe_connect_payouts`.",
      inputSchema: {},
      annotations: annotationsFor("stripe_connect_balance", "read"),
    },
    async () => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "stripe_connect_balance", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;
      const start = Date.now();
      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");
        const accountId = await getAccountId();
        if (!accountId) return err("No connected Stripe account found.");

        const result = await stripeGet<{
          available: Array<{ amount: number; currency: string }>;
          pending: Array<{ amount: number; currency: string }>;
        }>(stripeKey, `/v1/balance`, accountId);

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: "stripe_connect_balance",
          ocs_method: "stripe.balance.read",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(JSON.stringify({
          account_id: accountId,
          available: result.data.available ?? [],
          pending: result.data.pending ?? [],
        }));
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // stripe_connect_refund (HARD_BLOCK)
  // -------------------------------------------------------------------------

  server.registerTool(
    "stripe_connect_refund",
    {
      title: "Refund a Charge (moves real money)",
      description:
        "Refund money to a customer on one charge in the operator's connected Stripe account. This returns real funds and Stripe cannot reverse a refund once issued. " +
        "Two-step by design: call without `confirm_token` to get a token and a summary of what would be refunded, then call again with that token to execute. The token expires in 5 minutes. " +
        "Params: `charge_id` (Stripe charge id, `ch_...`), `amount_cents` (optional integer in the charge's smallest currency unit — omit for a full refund), " +
        "`reason` ('duplicate' | 'fraudulent' | 'requested_by_customer', optional), `confirm_token`. " +
        "Returns: the refund id and status. Requires `write` scope. " +
        "Do NOT use this to stop a charge that has not settled yet — decline it with `radar_review_decline`. " +
        "Do NOT use this to contest a chargeback — read the case with `stripe_connect_dispute_list` first, since refunding a disputed charge can cost the operator twice.",
      inputSchema: {
        charge_id: z.string().min(1).describe("Stripe charge ID (ch_...)."),
        amount_cents: z
          .number()
          .int()
          .min(1)
          .optional()
          .describe("Partial refund amount in cents. Omit for full refund."),
        reason: z.enum(["duplicate", "fraudulent", "requested_by_customer"]).optional(),
        confirm_token: z
          .string()
          .optional()
          .describe("Confirmation token from previous call. Required to execute."),
      },
      annotations: annotationsFor("stripe_connect_refund", "write"),
    },
    async ({ charge_id, amount_cents, reason, confirm_token }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "stripe_connect_refund", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;
      const start = Date.now();
      const toolName = "stripe_connect_refund";

      if (!confirm_token) {
        const token = await issueConfirmToken(env, props.sub, toolName);
        return ok(
          `HARD_BLOCK: Refund ${amount_cents ? `${amount_cents} cents on` : "(full) on"} charge ${charge_id} requires confirmation.\n` +
          `confirm_token: ${token}\n` +
          `Call again with confirm_token="${token}" to execute. Token expires in 5 minutes.`,
        );
      }

      const valid = await verifyConfirmToken(env, props.sub, toolName, confirm_token);
      if (!valid) {
        return err("Invalid or expired confirm_token. Call without token to get a new one.");
      }

      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");
        const accountId = await getAccountId();
        if (!accountId) return err("No connected Stripe account found.");

        const params: Record<string, string> = { charge: charge_id };
        if (amount_cents) params.amount = String(amount_cents);
        if (reason) params.reason = reason;

        const result = await stripePost<{
          id: string;
          amount: number;
          currency: string;
          reason: string | null;
          status: string;
        }>(
          stripeKey,
          "/v1/refunds",
          params,
          accountId,
          refundIdempotencyKey(props.sub, charge_id, amount_cents, reason),
        );

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: toolName,
          ocs_method: "stripe.refund.create",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(`Refund issued: ${result.data.id} — status: ${result.data.status}`);
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // stripe_connect_dispute_list
  // -------------------------------------------------------------------------

  server.registerTool(
    "stripe_connect_dispute_list",
    {
      title: "List Chargeback Disputes",
      description:
        "List chargebacks customers have raised with their bank against the operator's connected Stripe account. Read-only — responding to one is Dashboard work. " +
        "Params: `limit` (1-50, default 10), `status` (optional Stripe dispute status, e.g. 'needs_response', 'under_review'). " +
        "Returns: { disputes: [{ id, charge, amount, currency, status, reason, evidence_details.due_by }], has_more } — amount in the currency's smallest unit, `due_by` a Unix timestamp or null. " +
        "Do NOT use this for charges Stripe is holding for fraud review before they settle — those are `radar_review_list`. " +
        "Check this before refunding: refunding a disputed charge can cost the operator the amount twice.",
      inputSchema: {
        limit: z.number().int().min(1).max(50).default(10),
        status: z
          .string()
          .optional()
          .describe("Filter by dispute status (e.g. needs_response, under_review)."),
      },
      annotations: annotationsFor("stripe_connect_dispute_list", "read"),
    },
    async ({ limit, status }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "stripe_connect_dispute_list", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;
      const start = Date.now();
      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");
        const accountId = await getAccountId();
        if (!accountId) return err("No connected Stripe account found.");

        const params = new URLSearchParams({ limit: String(limit) });
        if (status) params.set("status", status);

        const result = await stripeGet<{
          data: Array<{
            id: string;
            charge: string;
            amount: number;
            currency: string;
            status: string;
            reason: string;
            evidence_details: { due_by: number | null };
          }>;
          has_more: boolean;
        }>(stripeKey, `/v1/disputes?${params.toString()}`, accountId);

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: "stripe_connect_dispute_list",
          ocs_method: "stripe.disputes.list",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(JSON.stringify({ disputes: result.data.data, has_more: result.data.has_more }));
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // radar_review_list
  // -------------------------------------------------------------------------

  server.registerTool(
    "radar_review_list",
    {
      title: "List Radar Fraud Reviews",
      description:
        "List the Stripe Radar fraud reviews waiting on a manual approve/decline decision, newest first. Reads only — deciding a review is a separate call. " +
        "Params: `open_only` (boolean, default true — false also returns already-decided reviews), `limit` (1-50, default 10). " +
        "Returns: { reviews: [{ id (`prv_...`), charge, reason, opened_reason, closed_reason, created, closed }], has_more }. " +
        "Call this first to get the `review_id` that `radar_review_approve` and `radar_review_decline` require. " +
        "Do NOT use this for chargebacks the customer has already raised with their bank — use `stripe_connect_dispute_list`.",
      inputSchema: {
        open_only: z.boolean().default(true).describe("If true, only returns open (undecided) reviews."),
        limit: z.number().int().min(1).max(50).default(10),
      },
      annotations: annotationsFor("radar_review_list", "read"),
    },
    async ({ open_only, limit }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "radar_review_list", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;

      // These call Stripe WITHOUT a Stripe-Account header, so they run on
      // Carrier's own platform account — the reviews and value lists they
      // touch belong to every customer at once, not to the caller. Scope is
      // not sufficient: an enterprise org legitimately holds admin on its
      // OWN data. Only Carrier itself may reach the platform account.
      //
      // Over stdio this refuses outright, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails
      // closed. That is the correct answer for a single-tenant process:
      // reaching Carrier's platform Radar surface is Console/hosted-MCP
      // work, not something a customer's own CLI should be able to do.
      if (!isPlatformOperator(env, props.org_id)) {
        return err(
          "This tool acts on Carrier's platform Stripe account and is " +
            "restricted to Carrier's own organization.",
        );
      }
      const start = Date.now();
      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");

        const params = new URLSearchParams({ limit: String(limit) });
        if (open_only) params.set("open", "true");

        const result = await stripeGet<{
          data: Array<{
            id: string;
            charge: string | { id: string };
            reason: string | null;
            opened_reason: string | null;
            closed_reason: string | null;
            created: number;
            closed: boolean;
          }>;
          has_more: boolean;
        }>(stripeKey, `/v1/radar/reviews?${params.toString()}`);

        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: "radar_review_list",
          ocs_method: "stripe.radar.reviews.list",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(JSON.stringify({ reviews: result.data.data, has_more: result.data.has_more }));
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // radar_review_approve (HARD_BLOCK)
  // -------------------------------------------------------------------------

  server.registerTool(
    "radar_review_approve",
    {
      title: "Approve Radar Review (captures charge)",
      description:
        "Clear a Stripe Radar fraud review so the held charge is captured and the money is taken. Approving overrides Radar's fraud signal, so a genuinely fraudulent charge approved here usually returns later as a chargeback plus a fee. " +
        "Two-step by design: call without `confirm_token` to get a token, then call again with it to execute. " +
        "Params: `review_id` (`prv_...`, from `radar_review_list`), `confirm_token`. Requires `admin` scope. " +
        "Do NOT call this to block a suspicious charge — that is `radar_review_decline`, the opposite outcome. " +
        "Do NOT call this to return money on a charge that already went through — that is `stripe_connect_refund`.",
      inputSchema: {
        review_id: z.string().min(1).describe("Stripe Radar review ID (prv_...)."),
        confirm_token: z.string().optional(),
      },
      annotations: annotationsFor("radar_review_approve", "admin"),
    },
    async ({ review_id, confirm_token }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "radar_review_approve", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;

      // These call Stripe WITHOUT a Stripe-Account header, so they run on
      // Carrier's own platform account — the reviews and value lists they
      // touch belong to every customer at once, not to the caller. Scope is
      // not sufficient: an enterprise org legitimately holds admin on its
      // OWN data. Only Carrier itself may reach the platform account.
      //
      // Over stdio this refuses outright, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails
      // closed. That is the correct answer for a single-tenant process:
      // reaching Carrier's platform Radar surface is Console/hosted-MCP
      // work, not something a customer's own CLI should be able to do.
      if (!isPlatformOperator(env, props.org_id)) {
        return err(
          "This tool acts on Carrier's platform Stripe account and is " +
            "restricted to Carrier's own organization.",
        );
      }
      const toolName = "radar_review_approve";
      const start = Date.now();

      if (!confirm_token) {
        const token = await issueConfirmToken(env, props.sub, toolName);
        return ok(
          `HARD_BLOCK: Approving review ${review_id} allows the charge to proceed.\n` +
          `confirm_token: ${token}\nCall again with confirm_token="${token}" to execute. Expires in 5 minutes.`,
        );
      }

      const valid = await verifyConfirmToken(env, props.sub, toolName, confirm_token);
      if (!valid) return err("Invalid or expired confirm_token.");

      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");

        const result = await stripePost<{ id: string; closed: boolean }>(
          stripeKey,
          `/v1/radar/reviews/${review_id}/approve`,
          {},
        );
        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: toolName,
          ocs_method: "stripe.radar.review.approve",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(`Review ${review_id} approved. Charge will proceed.`);
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // radar_review_decline (HARD_BLOCK)
  // -------------------------------------------------------------------------

  server.registerTool(
    "radar_review_decline",
    {
      title: "Decline Radar Review (blocks charge)",
      description:
        "Close a Stripe Radar fraud review as fraudulent, so the held charge is blocked and the customer is never billed. The customer sees a failed payment; if they were legitimate, they have to pay again. " +
        "Two-step by design: call without `confirm_token` to get a token, then call again with it to execute. " +
        "Params: `review_id` (`prv_...`, from `radar_review_list`), `confirm_token`. Requires `admin` scope. " +
        "Do NOT call this to let a charge through — that is `radar_review_approve`, the opposite outcome. " +
        "Do NOT call this on a charge that already succeeded; a review only exists while the charge is held, and returning settled money is `stripe_connect_refund`.",
      inputSchema: {
        review_id: z.string().min(1),
        confirm_token: z.string().optional(),
      },
      annotations: annotationsFor("radar_review_decline", "admin"),
    },
    async ({ review_id, confirm_token }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "radar_review_decline", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;

      // These call Stripe WITHOUT a Stripe-Account header, so they run on
      // Carrier's own platform account — the reviews and value lists they
      // touch belong to every customer at once, not to the caller. Scope is
      // not sufficient: an enterprise org legitimately holds admin on its
      // OWN data. Only Carrier itself may reach the platform account.
      //
      // Over stdio this refuses outright, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails
      // closed. That is the correct answer for a single-tenant process:
      // reaching Carrier's platform Radar surface is Console/hosted-MCP
      // work, not something a customer's own CLI should be able to do.
      if (!isPlatformOperator(env, props.org_id)) {
        return err(
          "This tool acts on Carrier's platform Stripe account and is " +
            "restricted to Carrier's own organization.",
        );
      }
      const toolName = "radar_review_decline";
      const start = Date.now();

      if (!confirm_token) {
        const token = await issueConfirmToken(env, props.sub, toolName);
        return ok(
          `HARD_BLOCK: Declining review ${review_id} will close/block the charge.\n` +
          `confirm_token: ${token}\nExpires in 5 minutes.`,
        );
      }

      const valid = await verifyConfirmToken(env, props.sub, toolName, confirm_token);
      if (!valid) return err("Invalid or expired confirm_token.");

      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");

        // Stripe closes a review by approving it with close_reason=fraudulent
        // or by calling close on the underlying charge. The /approve endpoint
        // with a reason is the supported path for platform-level decline.
        const result = await stripePost<{ id: string; closed: boolean }>(
          stripeKey,
          `/v1/radar/reviews/${review_id}/approve`,
          { reason: "fraudulent" },
        );
        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: toolName,
          ocs_method: "stripe.radar.review.decline",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(`Review ${review_id} declined.`);
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // radar_value_list_add (HARD_BLOCK)
  // -------------------------------------------------------------------------

  server.registerTool(
    "radar_value_list_add",
    {
      title: "Add Value to Radar List",
      description:
        "Add one value to an existing Stripe Radar value list, changing how every future charge is scored. " +
        "Whether the value ends up blocked or allowed depends entirely on which list you name in `value_list_id` — this tool does not choose, and putting a value on the allow list when you meant the block list silently lets fraud through. Confirm the list's purpose before calling. " +
        "Two-step by design: call without `confirm_token` to get a token, then call again with it to execute. " +
        "Params: `value_list_id` (`rsl_...`, an existing list), `value` (one email, IP address, card fingerprint, or ISO country code — one item per call), `confirm_token`. Requires `admin` scope. " +
        "Do NOT use this to decide a single charge that is already under review — use `radar_review_approve` or `radar_review_decline`. " +
        "Do NOT use this to create a list or a rule: only `value` is added, and rules cannot be edited through this API at all (see `radar_rule_toggle`).",
      inputSchema: {
        value_list_id: z.string().min(1).describe("Stripe Radar value list ID (rsl_...)."),
        value: z
          .string()
          .min(1)
          .describe("The value to add (email, IP address, country code, etc.)."),
        confirm_token: z.string().optional(),
      },
      annotations: annotationsFor("radar_value_list_add", "admin"),
    },
    async ({ value_list_id, value, confirm_token }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "radar_value_list_add", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;

      // These call Stripe WITHOUT a Stripe-Account header, so they run on
      // Carrier's own platform account — the reviews and value lists they
      // touch belong to every customer at once, not to the caller. Scope is
      // not sufficient: an enterprise org legitimately holds admin on its
      // OWN data. Only Carrier itself may reach the platform account.
      //
      // Over stdio this refuses outright, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails
      // closed. That is the correct answer for a single-tenant process:
      // reaching Carrier's platform Radar surface is Console/hosted-MCP
      // work, not something a customer's own CLI should be able to do.
      if (!isPlatformOperator(env, props.org_id)) {
        return err(
          "This tool acts on Carrier's platform Stripe account and is " +
            "restricted to Carrier's own organization.",
        );
      }
      const toolName = "radar_value_list_add";
      const start = Date.now();

      if (!confirm_token) {
        const token = await issueConfirmToken(env, props.sub, toolName);
        return ok(
          `HARD_BLOCK: Adding "${value}" to list ${value_list_id} will affect future charge decisions.\n` +
          `confirm_token: ${token}\nExpires in 5 minutes.`,
        );
      }

      const valid = await verifyConfirmToken(env, props.sub, toolName, confirm_token);
      if (!valid) return err("Invalid or expired confirm_token.");

      try {
        const stripeKey = env.STRIPE_SECRET_KEY;
        if (!stripeKey) return err("Stripe not configured");

        const result = await stripePost<{ id: string; value: string }>(
          stripeKey,
          "/v1/radar/value_list_items",
          { value_list: value_list_id, value },
        );
        if (!result.ok) return err(`Stripe error: ${JSON.stringify(result.data)}`);

        writeAudit(env, {
          tool_name: toolName,
          ocs_method: "stripe.radar.value_list.add",
          status: "ok",
          dry_run: false,
          duration_ms: Date.now() - start,
          sub: props.sub,
          reseller_id: props.reseller_id,
        });

        return ok(`Added "${value}" to Radar list ${value_list_id}.`);
      } catch (e) {
        Sentry.captureException(e);
        return err(`Error: ${e instanceof Error ? e.message : "unknown"}`);
      }
    },
  );

  // -------------------------------------------------------------------------
  // radar_rule_toggle
  // -------------------------------------------------------------------------

  server.registerTool(
    "radar_rule_toggle",
    {
      title: "Radar Rule Toggle (instructions only)",
      description:
        "Return the Stripe Dashboard steps for enabling or disabling a Radar rule. This tool changes nothing: Stripe has no public API for rule state, so the call always succeeds and the rule stays exactly as it was. Never report the rule as toggled. " +
        "Params: `rule_id` (Stripe Radar rule id), `enabled` (true for the enable instructions, false for disable). " +
        "Returns: the Dashboard URL and steps for a human to follow. Requires `admin` scope. " +
        "Do NOT use this when a change must actually take effect from an agent — the only Radar state reachable through this API is value-list membership, via `radar_value_list_add`.",
      inputSchema: {
        rule_id: z.string().min(1).describe("Stripe Radar rule ID."),
        enabled: z.boolean().describe("true = enable, false = disable."),
      },
      annotations: annotationsFor("radar_rule_toggle", "admin"),
    },
    async ({ rule_id, enabled }) => {
      // Call-time scope check. This file does not use wrapHandler,
      // so without this the tool is callable at any scope. See
      // scope-guard.ts.
      const denied = denyUnlessScoped(props, "radar_rule_toggle", STRIPE_CONNECT_TOOL_SCOPES);
      if (denied) return denied;

      // These call Stripe WITHOUT a Stripe-Account header, so they run on
      // Carrier's own platform account — the reviews and value lists they
      // touch belong to every customer at once, not to the caller. Scope is
      // not sufficient: an enterprise org legitimately holds admin on its
      // OWN data. Only Carrier itself may reach the platform account.
      //
      // Over stdio this refuses outright, because a local CLI has no Clerk
      // org and no CARRIER_PLATFORM_ORG_ID, so isPlatformOperator fails
      // closed. That is the correct answer for a single-tenant process:
      // reaching Carrier's platform Radar surface is Console/hosted-MCP
      // work, not something a customer's own CLI should be able to do.
      if (!isPlatformOperator(env, props.org_id)) {
        return err(
          "This tool acts on Carrier's platform Stripe account and is " +
            "restricted to Carrier's own organization.",
        );
      }
      // Stripe Radar rules are not manageable via the REST API — only the Dashboard.
      return ok(
        `Stripe Radar does not expose rule enable/disable via the public API.\n` +
        `To ${enabled ? "enable" : "disable"} rule ${rule_id}:\n` +
        `1. Open https://dashboard.stripe.com/radar/rules\n` +
        `2. Find rule ${rule_id} and toggle it ${enabled ? "on" : "off"}.\n\n` +
        `Note: If you need this automated, use the Radar for Platforms beta — contact Stripe support at https://support.stripe.com.`,
      );
    },
  );
}
